import { useApi } from '@/components/Functional/hooks-core/useApi'
import { useCampaignSlug } from '@/components/Functional/hooks-core/useCampaignSlug'
import { AppMachineContext } from '@/components/Functional/hooks-core/useRaundLifeCycle'
import useGeoLocation from '@/components/Functional/hooks/useGeoLocation'
import { useNotificationBadge } from '@/components/Functional/hooks/useNotificationBadge'
import { useStampPointLabelFromSheetTypes } from '@/components/Functional/useStampPointLabel'
import { BottomNavigation } from '@/components/Layout/BottomNavigation'
import { Header } from '@/components/Layout/Header'
import { ArPhotoOverlay } from '@/components/Models/ar-photo'
import { DialogArPhotoLocked, DialogAudioGuideLocked, DialogSpotsAudioGuide } from '@/components/Pages/spots/components'
import { DialogIlustmap } from '@/components/Pages/spots/components/list/DialogIlustmap'
import { SpotFilterRow } from '@/components/Pages/spots/components/list/SpotFilterRow'
import { SpotList } from '@/components/Pages/spots/components/list/SpotList'
import { CircleLoading } from '@/components/UI/CircleLoading'
import type { PillSelectOption } from '@/components/UI/PillSelect'
import { Skeleton } from '@/components/UI/Skeleton'
import { Tabs } from '@/components/UI/Tabs'
import {
  type PhotoFrameItem,
  type PhotoFrameLockReason,
  type SpotData,
  isPhotoFrameLocked,
  pickPhotoFrameAudio,
  resolvePhotoFrameLockReason,
  toPhotoFrameItems,
} from '@/schemas/v2/spot'
import { useTokenStore } from '@/store/useTokenStore'
import { useSelector } from '@xstate/react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSpotListStateMachine } from './useSpotListStateMachine'

/** スポット一覧タイプ（バックエンドの LIST_TYPE_* 定数に対応） */
const LIST_TYPE_MAP_AND_LIST = 1
const LIST_TYPE_MAP = 2
const LIST_TYPE_LIST = 3

// Leaflet は window に依存するため、ビルド時の Node.js 実行を回避するためにブラウザ側のみロードする
const SpotMap = dynamic(() => import('@/components/Pages/spots/components/list/SpotMap').then((m) => m.SpotMap), {
  ssr: false,
  loading: () => (
    <div className="h-full w-full">
      <Skeleton variant="rect" height={2000} />
    </div>
  ),
})

export const SpotListPage = () => {
  const { t } = useTranslation()
  const router = useRouter()
  const { gen } = useCampaignSlug()
  const geoLocation = useGeoLocation()

  const appActorRef = AppMachineContext.useActorRef()
  const isAuthenticated = useSelector(appActorRef, (s) => s.context.isAuthenticated)
  const campaignData = useSelector(appActorRef, (s) => s.context.campaignData)
  const { hasUnread } = useNotificationBadge(isAuthenticated)

  const campaignSheetTypes = campaignData?.sheetTypes ?? []
  const { label: stampPointLabel } = useStampPointLabelFromSheetTypes(campaignSheetTypes)

  const { list, attached } = useSpotListStateMachine({ isAuthenticated })

  const api = useApi()
  const idToken = useTokenStore((s) => s.idToken)
  const [arPhotoFrames, setArPhotoFrames] = useState<PhotoFrameItem[]>([])
  // ARフォトのロック理由（#3445）。文言をスタンプ未獲得 / 範囲外で出し分けるために保持する
  const [arPhotoLockReason, setArPhotoLockReason] = useState<PhotoFrameLockReason | null>(null)
  // AR起動時に自動再生する音声URL（#3261）。署名付きURLのためキャッシュせず起動直前の取得結果から保持する
  const [arPhotoAudio, setArPhotoAudio] = useState<string | null>(null)
  // ARフォトAPI呼び出し中の二重送信防止（描画不要なため useRef で管理）。
  // 一覧は複数スポットが並ぶため、スポット単位で保持しないと別スポットのタップまで握り潰す
  const arPhotoInFlightRef = useRef<Set<number>>(new Set())
  const [isMapDrawerOpen, setIsMapDrawerOpen] = useState(false)

  // GPS 更新等で頻繁に再レンダーされるページのため、参照を安定化して
  // memo された ArPhotoOverlay の不要な再レンダーを抑える。
  const handleArPhotoClose = useCallback(() => {
    list.send({ type: 'CLOSE_AR_PHOTO' })
    setArPhotoFrames([])
  }, [list.send])

  const illustrationMaps = campaignData?.illustrationMaps ?? []
  const hasImageMap = illustrationMaps.length > 0
  const imageMapLabel = campaignData?.mapCaption || 'PDF'

  // campaignData が null（ロード中）のうちは showMap / showList を false にする。
  // ロード前に SpotMap（Leaflet）がマウントされると dynamic import が不要に走るため、
  // ロード完了後に listType に応じて正しい表示を確定させる。
  const isCampaignLoaded = campaignData !== null
  const listType = campaignData?.listType ?? LIST_TYPE_MAP_AND_LIST
  const showMap = isCampaignLoaded && listType !== LIST_TYPE_LIST
  const showList = isCampaignLoaded && listType !== LIST_TYPE_MAP
  // マップ・リスト両方表示、またはイラストマップが存在する場合にタブを表示する
  const hasTabs = (showMap && showList) || (isCampaignLoaded && hasImageMap)

  const tabs = useMemo<string[]>(() => {
    if (listType === LIST_TYPE_MAP_AND_LIST) {
      return hasImageMap
        ? [t('spot.map', 'マップ'), t('spot.list', 'リスト'), imageMapLabel]
        : [t('spot.map', 'マップ'), t('spot.list', 'リスト')]
    }
    if (listType === LIST_TYPE_MAP) {
      return hasImageMap ? [t('spot.map', 'マップ'), imageMapLabel] : []
    }
    // LIST_TYPE_LIST
    return hasImageMap ? [t('spot.list', 'リスト'), imageMapLabel] : []
  }, [listType, hasImageMap, imageMapLabel, t])

  // ARフォトボタン押下時: GPS 付きで POST して結果に応じてフレームをセットまたはロックダイアログを表示
  // ログイン済みの場合は Authorization ヘッダー付き (api.auth) で呼び出し、バックエンドがスタンプ取得状況を確認できるようにする。
  const handleArPhotoClick = async (spot: SpotData) => {
    if (arPhotoInFlightRef.current.has(spot.id)) return
    arPhotoInFlightRef.current.add(spot.id)
    // 位置情報が取れていなくても起動を試みる（#3445）。
    // 早期 return するとボタンが無反応になり、距離制限が無いスポットの AR フォトまで開けなくなる。
    // 座標は送らず、範囲制限があるスポットはサーバー判定のロックダイアログで理由を案内する。
    const body = geoLocation.available ? { lat: geoLocation.lat, lon: geoLocation.lon } : {}
    try {
      const res =
        idToken !== null
          ? await api.auth.postSpotPhotoFrames(spot.id, body)
          : await api.guest.postSpotPhotoFrames(spot.id, body)
      if (!res) {
        // 理由を特定できないため既定（スタンプ未獲得）の文言にフォールバックする
        setArPhotoLockReason(null)
        attached.send({ type: 'OPEN_AR_PHOTO_LOCKED' })
        return
      }
      const entries = res.photoFrames
      if (isPhotoFrameLocked(entries)) {
        setArPhotoLockReason(resolvePhotoFrameLockReason(entries))
        attached.send({ type: 'OPEN_AR_PHOTO_LOCKED' })
      } else {
        setArPhotoFrames(toPhotoFrameItems(entries))
        setArPhotoAudio(pickPhotoFrameAudio(entries))
        list.send({ type: 'OPEN_AR_PHOTO', spot })
      }
    } catch {
      // 理由を特定できないため既定（スタンプ未獲得）の文言にフォールバックする
      setArPhotoLockReason(null)
      attached.send({ type: 'OPEN_AR_PHOTO_LOCKED' })
    } finally {
      arPhotoInFlightRef.current.delete(spot.id)
    }
  }

  // PillSelect に渡す options（「すべてのコース」を先頭に追加）。value はシートID文字列。
  // 購入連動ロック中（locked=true）のシートには 🔓 を付与する。
  const sheetOptions = useMemo<PillSelectOption[]>(
    () => [
      { value: '', label: t('spot.all_courses') },
      ...list.context.sheetFilterOptions.map((s) => ({
        value: String(s.id),
        label: s.locked ? `🔓 ${s.name}` : s.name,
      })),
    ],
    [list.context.sheetFilterOptions, t],
  )

  // #3414: 未購入スポットでも詳細ページへ遷移させる（ロックの提示・注意喚起はスポット詳細に一元化）。
  // カードの灰色表示も行わず、購入前であることはシート名の 🔓 のみで示す。

  const handleTabChange = (index: number) => {
    if (listType === LIST_TYPE_MAP_AND_LIST) {
      if (hasImageMap && index === 2) list.send({ type: 'SHOW_PDF' })
      else if (index === 0) list.send({ type: 'SELECT_MAP' })
      else list.send({ type: 'SELECT_LIST' })
    } else if (listType === LIST_TYPE_MAP) {
      // tab 0 = マップ（state変更不要）、tab 1 = イラストマップ
      if (hasImageMap && index === 1) list.send({ type: 'SHOW_PDF' })
    } else {
      // LIST_TYPE_LIST: tab 0 = リスト（state変更不要）、tab 1 = イラストマップ
      if (hasImageMap && index === 1) list.send({ type: 'SHOW_PDF' })
    }
  }

  // マップ Drawer 展開中、またはルートマップ(PDF)ダイアログ表示中は、
  // 地図上に重なるフィルター行（コース選択・タグ Pill）を操作不可にする
  const isFilterRowBlocked = isMapDrawerOpen || list.context.showPdfDialog

  return (
    <>
      <Header
        title={t('common.spot', 'スポット')}
        onNotification={() => router.push(gen('/news'))}
        hasUnreadBadge={hasUnread}
      />
      {hasTabs && <Tabs tabs={tabs} activeIndex={list.context.activeTab} onChange={handleTabChange} />}
      <main
        style={{ height: hasTabs ? 'calc(100dvh - 116px - 45px)' : 'calc(100dvh - 116px)', paddingBottom: 0 }}
        className="relative w-full overflow-hidden"
      >
        {showMap && (listType !== LIST_TYPE_MAP_AND_LIST || !hasTabs || list.context.activeTab === 0) ? (
          <>
            {/* SpotMap は isLoading と独立してマウントし、地図を即座に表示する。
                スポットマーカーは API 完了後（isLoading=false）に追加される。 */}
            <SpotMap
              spots={list.isLoading ? [] : list.context.filteredSpots}
              onArPhotoClick={handleArPhotoClick}
              geoLocation={geoLocation}
              stampImageBySpotId={list.context.stampImageBySpotId}
              mapCenter={campaignData?.mapCenter}
              mapPinImage={campaignData?.mapPinImage}
              acquiredSpotIds={list.context.acquiredSpotIds}
              onDrawerOpenChange={setIsMapDrawerOpen}
              sheetNameById={list.sheetNameById}
            />
            {/* ローディング中は地図の上にオーバーレイ表示 */}
            {list.isLoading && (
              <div className="absolute inset-0 z-[2000] flex items-center justify-center bg-white/60">
                <CircleLoading />
              </div>
            )}
            {/* フィルター行：ARフォトが非表示の場合のみ地図の上にオーバーレイ表示 */}
            {!list.isLoading && list.context.arPhotoSpot === null && (
              <div
                className={`absolute left-0 top-0 w-full ${isFilterRowBlocked ? 'pointer-events-none z-0' : 'z-[1000]'}`}
              >
                <SpotFilterRow
                  sheetOptions={sheetOptions}
                  selectedSheet={list.context.selectedSheet}
                  onSheetChange={(sheet) => list.send({ type: 'SELECT_SHEET', sheet })}
                  tags={list.context.uniqueTags}
                  selectedTagId={list.context.selectedTagId}
                  onTagClick={(tagId) => list.send({ type: 'SELECT_TAG', tagId })}
                />
              </div>
            )}
          </>
        ) : (
          <div className="flex h-full flex-col overflow-hidden">
            {list.isLoading ? (
              <CircleLoading />
            ) : (
              <>
                <SpotFilterRow
                  sheetOptions={sheetOptions}
                  selectedSheet={list.context.selectedSheet}
                  onSheetChange={(sheet) => list.send({ type: 'SELECT_SHEET', sheet })}
                  tags={list.context.uniqueTags}
                  selectedTagId={list.context.selectedTagId}
                  onTagClick={(tagId) => list.send({ type: 'SELECT_TAG', tagId })}
                />
                <div className="min-h-0 flex-1 overflow-hidden">
                  <SpotList
                    spots={list.context.filteredSpots}
                    onArPhotoClick={handleArPhotoClick}
                    geoLocation={geoLocation}
                    sheetNameById={list.sheetNameById}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </main>
      {list.context.showPdfDialog && (
        <DialogIlustmap
          onClose={() => list.send({ type: 'CLOSE_PDF' })}
          scrollLock
          title={imageMapLabel}
          ilustMaps={(campaignData?.illustrationMaps ?? []).map((m) => ({
            caption: m.name ?? m.url,
            url: m.url,
          }))}
        />
      )}
      <BottomNavigation activeItem="spot" />
      <ArPhotoOverlay
        open={list.context.arPhotoSpot !== null}
        photoFrames={arPhotoFrames}
        audioUrl={arPhotoAudio}
        onClose={handleArPhotoClose}
        snsHashtag=""
      />
      {/* ── 付帯ダイアログ（ARフォトロック・音声ガイド） ── */}
      {attached.isArPhotoLocked && (
        <DialogArPhotoLocked
          scrollLock
          stampLabel={stampPointLabel}
          reason={arPhotoLockReason}
          locationUnavailable={geoLocation.isLoaded && !geoLocation.available}
          onClose={() => attached.send({ type: 'CLOSE' })}
        />
      )}
      {attached.isAudioGuide && <DialogSpotsAudioGuide scrollLock onClose={() => attached.send({ type: 'CLOSE' })} />}
      {attached.isAudioGuideLocked && (
        <DialogAudioGuideLocked scrollLock onClose={() => attached.send({ type: 'CLOSE' })} />
      )}
    </>
  )
}
